import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { proposalsContent } from '../data/mock';
import AnimatedSection from '../components/common/AnimatedSection';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, Share2, Heart,
  Landmark, TrendingUp, Users, ChevronDown, ChevronUp,
  AlertCircle, Lightbulb, Wrench, Target
} from 'lucide-react';

const PropositionsPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const content = proposalsContent[language] || proposalsContent.fr;
  const [expandedAxe, setExpandedAxe] = useState('institutional');
  const [expandedProposal, setExpandedProposal] = useState(null);

  const axeIcons = {
    institutional: Landmark,
    economic: TrendingUp,
    social: Users
  };

  const axeColors = {
    institutional: 'from-[#5B2D8E] to-[#7C4DAF]',
    economic: 'from-[#FFC107] to-[#FFD54F]',
    social: 'from-[#0D9488] to-[#14B8A6]'
  };

  const tagColors = {
    institutional: isDark ? 'bg-[#5B2D8E]/30 text-[#9B7FCE]' : 'bg-[#5B2D8E]/10 text-[#5B2D8E]',
    economic: isDark ? 'bg-[#FFC107]/20 text-[#FFC107]' : 'bg-[#FFC107]/20 text-[#B8860B]',
    social: isDark ? 'bg-[#0D9488]/30 text-[#5EEAD4]' : 'bg-[#0D9488]/10 text-[#0D9488]'
  };

  const sectionIcons = {
    diagnostic: AlertCircle,
    proposition: Lightbulb,
    implementation: Wrench,
    impact: Target
  };

  const sectionColors = {
    diagnostic: 'text-red-500',
    proposition: 'text-[#5B2D8E]',
    implementation: 'text-[#FFC107]',
    impact: 'text-green-500'
  };

  const toggleProposal = (proposalKey) => {
    setExpandedProposal(expandedProposal === proposalKey ? null : proposalKey);
  };

  // Render structured format (Diagnostic → Proposition → Implementation → Impact)
  const renderStructuredContent = (proposal) => {
    const sections = content.sections || proposalsContent.fr.sections;
    
    return (
      <div className="space-y-4">
        {/* Diagnostic */}
        <div className={`p-4 rounded-lg ${isDark ? 'bg-red-500/10' : 'bg-red-50'} border-l-4 border-red-500`}>
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <h4 className="font-semibold text-red-600 dark:text-red-400">{sections.diagnostic}</h4>
          </div>
          <p className="text-theme-secondary text-sm leading-relaxed">{proposal.diagnostic}</p>
        </div>

        {/* Proposition */}
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#5B2D8E]/10' : 'bg-[#5B2D8E]/5'} border-l-4 border-[#5B2D8E]`}>
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-5 h-5 text-[#5B2D8E]" />
            <h4 className={`font-semibold ${isDark ? 'text-[#9B7FCE]' : 'text-[#5B2D8E]'}`}>{sections.proposition}</h4>
          </div>
          <p className="text-theme-secondary text-sm leading-relaxed">{proposal.proposition}</p>
        </div>

        {/* Implementation */}
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#FFC107]/10' : 'bg-[#FFC107]/5'} border-l-4 border-[#FFC107]`}>
          <div className="flex items-center gap-2 mb-2">
            <Wrench className="w-5 h-5 text-[#B8860B]" />
            <h4 className={`font-semibold ${isDark ? 'text-[#FFC107]' : 'text-[#B8860B]'}`}>{sections.implementation}</h4>
          </div>
          <p className="text-theme-secondary text-sm leading-relaxed whitespace-pre-line">{proposal.implementation}</p>
        </div>

        {/* Impact */}
        <div className={`p-4 rounded-lg ${isDark ? 'bg-green-500/10' : 'bg-green-50'} border-l-4 border-green-500`}>
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-5 h-5 text-green-500" />
            <h4 className="font-semibold text-green-600 dark:text-green-400">{sections.impact}</h4>
          </div>
          <p className="text-theme-secondary text-sm leading-relaxed">{proposal.impact}</p>
        </div>
      </div>
    );
  };

  // Render legacy format (fullText)
  const renderLegacyContent = (proposal) => {
    return (
      <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1C1917]' : 'bg-white'} border-l-4 border-[#5B2D8E]`}>
        <p className="text-theme-secondary whitespace-pre-line text-sm leading-relaxed" style={{ fontFamily: 'var(--font-serif)' }}>
          {proposal.fullText}
        </p>
      </div>
    );
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className={`py-20 ${isDark ? 'bg-[#1C1917]' : 'bg-gradient-to-br from-[#FAFAF9] to-white'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection>
              <span className="caption mb-4 block">Nos axes</span>
              <h1 className="heading-1 mb-6">{content.title}</h1>
              <div className="divider mx-auto mb-8" />
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <p className="body-large max-w-2xl mx-auto">
                {content.subtitle}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Axes Navigation */}
      <section className={`py-6 sticky top-20 z-30 border-b ${
        isDark 
          ? 'bg-[#0C0A09]/90 backdrop-blur-xl border-white/10' 
          : 'bg-white/90 backdrop-blur-xl border-stone-200'
      }`}>
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-3">
            {content.axes.map((axe) => {
              const Icon = axeIcons[axe.id];
              return (
                <button
                  key={axe.id}
                  onClick={() => {
                    setExpandedAxe(axe.id);
                    setExpandedProposal(null);
                  }}
                  className={`flex items-center gap-2 px-5 py-3 rounded-full font-semibold transition-all ${
                    expandedAxe === axe.id
                      ? 'bg-[#5B2D8E] text-white'
                      : isDark
                        ? 'bg-white/10 text-stone-300 hover:bg-white/20'
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="hidden sm:inline">{axe.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Propositions Content */}
      <section className={`section-lg ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {content.axes.map((axe) => {
              const Icon = axeIcons[axe.id];
              const isExpanded = expandedAxe === axe.id;
              
              return (
                <AnimatedSection key={axe.id}>
                  <motion.div 
                    className={`mb-6 rounded-2xl overflow-hidden ${
                      isExpanded 
                        ? isDark ? 'bg-[#1C1917] shadow-xl' : 'bg-white shadow-lg'
                        : isDark ? 'bg-[#1C1917]/50' : 'bg-white/50'
                    } border ${isDark ? 'border-white/10' : 'border-stone-200'}`}
                    layout
                  >
                    {/* Axe Header */}
                    <button
                      onClick={() => {
                        setExpandedAxe(isExpanded ? null : axe.id);
                        setExpandedProposal(null);
                      }}
                      className="w-full p-6 flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${axeColors[axe.id]} flex items-center justify-center`}>
                          <Icon className={`w-7 h-7 ${axe.id === 'economic' ? 'text-[#1C1917]' : 'text-white'}`} />
                        </div>
                        <div>
                          <h2 className="text-xl lg:text-2xl font-bold text-theme-primary" style={{ fontFamily: 'var(--font-heading)' }}>
                            {axe.title}
                          </h2>
                          <p className="text-theme-secondary text-sm mt-1">{axe.description}</p>
                          <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs ${isDark ? 'bg-white/10 text-stone-400' : 'bg-stone-100 text-stone-500'}`}>
                            {axe.proposals.length} proposition{axe.proposals.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-6 h-6 text-[#5B2D8E]" />
                      ) : (
                        <ChevronDown className="w-6 h-6 text-theme-muted" />
                      )}
                    </button>

                    {/* Proposals List */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 pb-6 space-y-4">
                            {axe.proposals.map((proposal, index) => {
                              const proposalKey = `${axe.id}-${index}`;
                              const isProposalExpanded = expandedProposal === proposalKey;
                              const hasContent = proposal.hasStructuredFormat || proposal.fullText;
                              
                              return (
                                <motion.div
                                  key={index}
                                  initial={{ x: -20, opacity: 0 }}
                                  animate={{ x: 0, opacity: 1 }}
                                  transition={{ delay: index * 0.1 }}
                                  className={`rounded-xl overflow-hidden ${
                                    isDark ? 'bg-[#292524]' : 'bg-[#FAFAF9]'
                                  } border ${isDark ? 'border-white/5' : 'border-stone-100'}`}
                                >
                                  <div className="p-5">
                                    {/* Tag */}
                                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${tagColors[axe.id]}`}>
                                      {proposal.tag}
                                    </span>
                                    
                                    {/* Title */}
                                    <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`}>
                                      {proposal.title}
                                    </h3>

                                    {/* Subtitle (for structured format) */}
                                    {proposal.subtitle && (
                                      <p className="text-theme-secondary text-sm mb-3 italic">
                                        {proposal.subtitle}
                                      </p>
                                    )}
                                    
                                    {/* Description (for legacy format) */}
                                    {proposal.description && !proposal.hasStructuredFormat && (
                                      <p className="text-theme-secondary mb-4">
                                        {proposal.description}
                                      </p>
                                    )}

                                    {/* Structured Format Badge */}
                                    {proposal.hasStructuredFormat && !isProposalExpanded && (
                                      <div className="flex flex-wrap gap-2 mb-4">
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'}`}>
                                          <AlertCircle className="w-3 h-3" /> Diagnostic
                                        </span>
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${isDark ? 'bg-[#5B2D8E]/20 text-[#9B7FCE]' : 'bg-[#5B2D8E]/10 text-[#5B2D8E]'}`}>
                                          <Lightbulb className="w-3 h-3" /> Proposition
                                        </span>
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${isDark ? 'bg-[#FFC107]/20 text-[#FFC107]' : 'bg-[#FFC107]/10 text-[#B8860B]'}`}>
                                          <Wrench className="w-3 h-3" /> Mise en œuvre
                                        </span>
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'}`}>
                                          <Target className="w-3 h-3" /> Impact
                                        </span>
                                      </div>
                                    )}

                                    {/* Expanded Content */}
                                    <AnimatePresence>
                                      {isProposalExpanded && hasContent && (
                                        <motion.div
                                          initial={{ height: 0, opacity: 0 }}
                                          animate={{ height: 'auto', opacity: 1 }}
                                          exit={{ height: 0, opacity: 0 }}
                                          transition={{ duration: 0.2 }}
                                          className="overflow-hidden mb-4"
                                        >
                                          {proposal.hasStructuredFormat 
                                            ? renderStructuredContent(proposal)
                                            : renderLegacyContent(proposal)
                                          }
                                        </motion.div>
                                      )}
                                    </AnimatePresence>
                                    
                                    {/* Action buttons */}
                                    <div className="flex flex-wrap gap-3">
                                      {hasContent && (
                                        <button 
                                          onClick={() => toggleProposal(proposalKey)}
                                          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                            isProposalExpanded
                                              ? 'bg-[#5B2D8E] text-white'
                                              : isDark 
                                                ? 'bg-[#5B2D8E]/20 text-[#9B7FCE] hover:bg-[#5B2D8E]/30' 
                                                : 'bg-[#5B2D8E]/10 text-[#5B2D8E] hover:bg-[#5B2D8E]/20'
                                          }`}
                                        >
                                          {isProposalExpanded ? (
                                            <>
                                              <ChevronUp className="w-4 h-4" />
                                              {content.cta.collapse}
                                            </>
                                          ) : (
                                            <>
                                              <ChevronDown className="w-4 h-4" />
                                              {content.cta.readMore}
                                            </>
                                          )}
                                        </button>
                                      )}
                                      <button className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                        isDark 
                                          ? 'bg-white/10 text-stone-300 hover:bg-white/20' 
                                          : 'bg-white text-stone-600 hover:bg-stone-50 border border-stone-200'
                                      }`}>
                                        <Share2 className="w-4 h-4" />
                                        {content.cta.share}
                                      </button>
                                      <Link 
                                        to="/rejoindre"
                                        className="flex items-center gap-2 px-4 py-2 bg-[#5B2D8E] rounded-full text-sm text-white font-medium hover:bg-[#7C4DAF] transition-colors"
                                      >
                                        <Heart className="w-4 h-4" />
                                        {content.cta.contribute}
                                      </Link>
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#5B2D8E] to-[#7C4DAF] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#FFC107] rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <h2 className="heading-2 text-white mb-6">
                Participez à l'élaboration
              </h2>
              <p className="text-xl text-white/80 mb-10">
                Nos propositions s'enrichissent de vos contributions. Rejoignez le mouvement.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/rejoindre" 
                  className="btn-secondary"
                >
                  Devenir contributeur
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/notre-posture" 
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-bold rounded-full border border-white/30 hover:bg-white/20 transition-all"
                >
                  Notre posture
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PropositionsPage;
